import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-20 pb-10 md:pb-14 bg-background-100 min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center mb-4">
                <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
              </div>
              <h2 className="text-lg font-semibold text-foreground-900 mb-2">文章未找到</h2>
              <p className="text-sm text-foreground-500 mb-4">您要查看的内容不存在或已被移除</p>
              <Link
                to="/"
                className="px-5 py-2 text-sm font-medium text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer inline-block"
              >
                返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${article.title} - 蜜桃视频 | 蜜桃视频官网`}
        description={article.summary}
        keywords={`蜜桃视频,${article.category},${article.tags.slice(0, 3).join(',')}`}
        canonicalUrl={`https://www.mitaovideo.com/article/${article.id}`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: '蜜桃视频',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '蜜桃视频主页', item: 'https://www.mitaovideo.com/' },
              { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.mitaovideo.com/category/${article.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.mitaovideo.com/article/${article.id}` },
            ],
          },
        ]}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20 pb-10 md:pb-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '蜜桃视频主页', path: '/' },
                { label: article.category, path: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <article className="mt-8 bg-background-50 rounded-lg p-6 md:p-10 border border-background-200/70">
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-calendar-line mr-1"></i>{article.date}
                  </span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-time-line mr-1"></i>{article.readTime}
                  </span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>{article.views}
                  </span>
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading leading-snug">
                  {article.title}
                </h1>
                <div className="flex items-center gap-2 mt-4 text-sm text-foreground-600">
                  <i className="ri-user-line"></i>
                  <span>{article.author}</span>
                </div>
              </header>

              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 pt-6 border-t border-background-200/70">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-5 py-2 text-sm font-medium text-primary-500 border border-primary-300 rounded-full hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-1"></i>返回{article.category}
              </Link>
              <Link
                to="/"
                className="px-5 py-2 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-all whitespace-nowrap"
              >
                返回蜜桃视频主页
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}